import { useState, createContext, useContext, ReactNode, useId, useRef, useEffect } from 'react'

interface AccordionContextValue {
  openItems: Set<string>
  toggle: (id: string) => void
  allowMultiple: boolean
}

const AccordionContext = createContext<AccordionContextValue | null>(null)

interface AccordionProps {
  children: ReactNode
  allowMultiple?: boolean
  defaultOpen?: string[]
  className?: string
}

export function Accordion({
  children,
  allowMultiple = false,
  defaultOpen = [],
  className = '',
}: AccordionProps) {
  const [openItems, setOpenItems] = useState<Set<string>>(
    new Set(defaultOpen)
  )

  const toggle = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        if (!allowMultiple) {
          next.clear()
        }
        next.add(id)
      }
      return next
    })
  }

  return (
    <AccordionContext.Provider value={{ openItems, toggle, allowMultiple }}>
      <div className={`divide-y divide-slate-200 ${className}`}>{children}</div>
    </AccordionContext.Provider>
  )
}

interface AccordionItemRenderProps {
  isOpen: boolean
  toggle: () => void
  id: string
}

interface AccordionItemProps {
  children: ReactNode | ((props: AccordionItemRenderProps) => ReactNode)
  id?: string
  className?: string
}

export function AccordionItem({
  children,
  id: providedId,
  className = '',
}: AccordionItemProps) {
  const generatedId = useId()
  const id = providedId || generatedId
  const context = useContext(AccordionContext)

  if (!context) {
    throw new Error('AccordionItem must be used within an Accordion')
  }

  const isOpen = context.openItems.has(id)

  return (
    <div className={className} data-state={isOpen ? 'open' : 'closed'}>
      {typeof children === 'function'
        ? children({ isOpen, toggle: () => context.toggle(id), id })
        : children}
    </div>
  )
}

interface AccordionTriggerProps {
  children: ReactNode
  onClick?: () => void
  isOpen?: boolean
  className?: string
}

export function AccordionTrigger({
  children,
  onClick,
  isOpen,
  className = '',
}: AccordionTriggerProps) {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(isOpen)

  // Removed auto-scroll - was causing header/buttons to become hidden
  // Users can scroll manually if needed
  useEffect(() => {
    wasOpen.current = isOpen
  }, [isOpen])

  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={onClick}
      className={`
        w-full flex items-center justify-between
        py-4 text-left
        font-semibold text-slate-900
        hover:text-primary
        transition-colors duration-150
        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset
        ${className}
      `}
      aria-expanded={isOpen}
    >
      {children}
      <svg
        className={`
          w-5 h-5 text-slate-400
          transition-transform duration-200
          ${isOpen ? 'rotate-180' : ''}
        `}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>
  )
}

interface AccordionContentProps {
  children: ReactNode
  isOpen?: boolean
  className?: string
}

export function AccordionContent({
  children,
  isOpen,
  className = '',
}: AccordionContentProps) {
  if (!isOpen) return null

  return (
    <div
      className={`pb-4 ${className}`}
      role="region"
    >
      {children}
    </div>
  )
}
